/**
 * A product shown off on a proposal: photographs of it installed, with what
 * it does beside each.
 *
 * Jonathon, 2026-10-01, with the Pro Flo design handoff: "whenever we select
 * gutter guard upgrade or create an estimate for gutter guards i want this to
 * show on the estimate to the customer so they can see what it looks like".
 *
 * A gutter guard is the upgrade a homeowner can least picture: it sits on top
 * of a gutter they never look at, and "micromesh" means nothing until they see
 * one. So the proposal shows it, once, whenever the document offers one.
 *
 * **The words are the manufacturer's, and only theirs.** The handoff lists the
 * claims Pro Flo makes on proflousa.com and says not to add any. In
 * particular nothing here promises that gutters never need cleaning again, or
 * any other maintenance-free promise, and there is no fire rating until the
 * real one is confirmed.
 *
 * The photograph paths are frozen into a proposal's snapshot at send (ADR
 * 0032), so a file under `apps/web/public/product-showcases` is never renamed
 * or removed once shipped (ADR 0237). `product-showcases-manifest.json` beside
 * the carousel lists every one.
 */

/** One slide: a photograph and what it shows, or the warranty panel. */
export interface ShowcaseSlide {
  /** The small copper line over the headline. */
  kicker: string;
  headline: string;
  /** A paragraph, or a list of points, or both. */
  body?: string;
  points?: string[];
  /** Path under the web app's `public/`. Absent on the warranty panel. */
  image?: string;
  /** Which part of the photograph to keep when it is cropped. */
  imagePosition?: string;
  alt?: string;
  /** The warranty panel's one big figure, drawn on copper with no photo. */
  figure?: string;
}

export interface ProductShowcase {
  key: string;
  /** The product, as the heading over the carousel. */
  product: string;
  /** The two short lines beside the heading. */
  tagline: [string, string];
  slides: ShowcaseSlide[];
  /** Under the carousel. */
  footnote: string;
}

const FOLDER = "/product-showcases/pro-flo";

export const PRO_FLO_SHOWCASE: ProductShowcase = {
  key: "pro-flo",
  product: "Pro Flo Gutter Guards",
  tagline: ["Stainless mesh · Aluminum frame", "45-year transferable warranty"],
  slides: [
    {
      kicker: "The System",
      headline: "Built to keep water moving",
      body: "Pro Flo sits over your gutters and filters rainwater through a stainless steel mesh. Water goes in. Leaves, pine needles and twigs stay out.",
      image: `${FOLDER}/proflo-01-hillside-roofline.jpg`,
      imagePosition: "50% 40%",
      alt: "Pro Flo gutter guard installed along a hillside roofline",
    },
    {
      kicker: "Stainless Steel Mesh",
      headline: "Water in. Debris out.",
      points: [
        "Rainwater drains through a sturdy stainless steel mesh",
        "Leaves, pine needles and twigs stay on top of the panel",
        "Testing shows it keeps filtering when covered with wet leaves",
      ],
      image: `${FOLDER}/proflo-02-mesh-closeup.jpg`,
      imagePosition: "55% 50%",
      alt: "Close-up of Pro Flo stainless steel mesh",
    },
    {
      kicker: "Patent-Pending U-Bend",
      headline: "Designed to stop overflow",
      body: "Pro Flo's U-Bend technology helps keep water from running over the front of your gutters. Its water capacity exceeds U.S. record rainfall amounts.",
      image: `${FOLDER}/proflo-03-front-edge.jpg`,
      imagePosition: "50% 55%",
      alt: "Pro Flo front edge on a black gutter",
    },
    {
      kicker: "Materials",
      headline: "Aluminum & stainless. Nothing to rust.",
      points: [
        "Strong extruded aluminum frame",
        "Won't rust, rot or distort",
        "Expands and contracts with your gutters",
      ],
      image: `${FOLDER}/proflo-04-aluminum-frame.jpg`,
      imagePosition: "50% 60%",
      alt: "Pro Flo extruded aluminum frame fastened to the gutter",
    },
    {
      kicker: "Fit & Install",
      headline: "Works with the gutters you have",
      points: [
        "Fits 5″ and 6″ gutters. No replacement unless they're damaged",
        "Works with shingle, metal, shake or tile roofs at any pitch",
        "Professionally installed by a trained contractor",
        "Keeps gutters free-flowing and adds years of life to the system",
      ],
      image: `${FOLDER}/proflo-05-full-run.jpg`,
      imagePosition: "50% 45%",
      alt: "Pro Flo installed along a full gutter run",
    },
    {
      kicker: "Warranty",
      headline: "Year transferable warranty",
      body: "If you sell the house, the coverage goes with it.",
      figure: "45",
    },
  ],
  footnote: "Product details: Pro Flo USA. Warranty terms per manufacturer.",
};

/**
 * Whether a product on the document is a gutter guard.
 *
 * Matched on the name, because that is what a document carries. Every gutter
 * guard we sell is Pro Flo; the catalogue's one is "Premium Micromesh Stainless
 * Steel Gutter Guards", and a hand-typed line has read "Premium shurflo Gutter
 * Guards". The gutters themselves are not ("Seamless 6" Gutters"), which is
 * the other half of `isGutterProduct` in `gutterColors.ts`.
 */
export function isGutterGuardProduct(name: string): boolean {
  return /gutter\s*guards?\b/i.test(name);
}
